import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import styles from './SupplierOrderFulfillModal.module.scss';
import FulfillmentFulfillForm from '../../blocks/FulfillmentFulfillForm';
import { canFulfill } from '../../../utils/fulfillmentHelper';

function findFulfillmentLine(fulfillment, variantId) {
  return fulfillment.fulfillment_lines.find((fulfillmentLine) => fulfillmentLine.variant_id === variantId);
}

function getFulfillmentLinesIdentifiers(fulfillmentsData) {
  const identifiers = [];
  fulfillmentsData.forEach((fulfillmentData) => {
    fulfillmentData.fulfillment.fulfillment_lines.forEach((fulfillmentLine) => {
      const { variant } = fulfillmentLine;
      if (variant.sku) {
        identifiers[fulfillmentLine.id] = {
          type: 'sku',
          id: fulfillmentLine.variant.sku,
        };
      } else if (variant.upc) {
        identifiers[fulfillmentLine.id] = {
          type: 'upc',
          id: fulfillmentLine.variant.upc,
        };
      } else if (variant.ean) {
        identifiers[fulfillmentLine.id] = {
          type: 'ean',
          id: fulfillmentLine.variant.ean,
        };
      } else if (variant.id_in_source) {
        identifiers[fulfillmentLine.id] = {
          type: 'id_in_source',
          id: fulfillmentLine.variant.id_in_source,
        };
      }
    });
  });

  return identifiers;
}

function SupplierOrderFulfillModal(props) {
  const { opened, onClose, order, supplierId } = props;
  const { retailer_order_id } = order;

  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const onUpdate = useCallback((fulfillmentId, fulfillmentData) => {
    setData((prev) => ({
      ...prev,
      [fulfillmentId]: fulfillmentData,
    }));

    setError(null);
  }, []);

  const fulfillmentsData = useMemo(() => {
    function findOrderLine(variantId) {
      return order.order_lines.find((orderLine) => orderLine.variant_id === variantId);
    }

    return order.fulfillments
      .filter((fulfillment) => fulfillment.supplier_id === supplierId)
      .map((fulfillment) => {
        fulfillment.fulfillment_lines.map((fulfillmentLine) => {
          fulfillmentLine.orderLine = findOrderLine(fulfillmentLine.variant_id);
          return fulfillmentLine;
        });

        const fulfillmentData = {
          shipments: [],
          rejections: [],
          fulfillment,
        };

        if (fulfillment.fulfillment_shipments) {
          fulfillment.fulfillment_shipments.forEach((fulfillmentShipment) => {
            const shipment = {
              trackingNumber: fulfillmentShipment.tracking_number,
              shippingMethod: fulfillmentShipment.shipping_method_code || fulfillment.shipping_method_code,
              quantities: {},
            };

            fulfillmentShipment.fulfillment_shipment_lines.forEach((fulfillmentShipmentLine) => {
              const fulfillmentLine = findFulfillmentLine(fulfillment, fulfillmentShipmentLine.variant_id);
              if (fulfillmentLine) {
                shipment.quantities[fulfillmentLine.id] = fulfillmentShipmentLine.quantity;
              }
            });

            fulfillmentData.shipments.push(shipment);
          });
        }

        if (fulfillment.fulfillment_partial_rejections) {
          fulfillment.fulfillment_partial_rejections.forEach((fulfillmentPartialRejection) => {
            const rejection = {
              quantities: {},
            };

            fulfillmentPartialRejection.fulfillment_partial_reject_lines.forEach((fulfillmentPartialRejectionLine) => {
              const fulfillmentLine = findFulfillmentLine(fulfillment, fulfillmentPartialRejectionLine.variant_id);
              if (fulfillmentLine) {
                rejection.quantities[fulfillmentLine.id] = fulfillmentPartialRejectionLine.quantity;
              }
            });

            fulfillmentData.rejections.push(rejection);
          });
        }

        return fulfillmentData;
      });
  }, [order, supplierId]);

  const areFullyFulfilled = useMemo(() => {
    const areFullyFulfilled = {};

    for (let fulfillmentData of fulfillmentsData) {
      areFullyFulfilled[fulfillmentData.fulfillment.id] = true;

      const quantities = {};

      for (let fulfillmentLine of fulfillmentData.fulfillment.fulfillment_lines) {
        quantities[fulfillmentLine.id] = 0;
      }

      if (!fulfillmentData.shipments.length && !fulfillmentData.rejections.length) {
        areFullyFulfilled[fulfillmentData.fulfillment.id] = false;
        continue;
      }

      fulfillmentData.shipments.forEach((shipment) => {
        Object.keys(shipment.quantities).forEach((fulfillmentLineId) => {
          quantities[fulfillmentLineId] = (quantities[fulfillmentLineId] || 0) + shipment.quantities[fulfillmentLineId];
        });
      });

      fulfillmentData.rejections.forEach((rejection) => {
        Object.keys(rejection.quantities).forEach((fulfillmentLineId) => {
          quantities[fulfillmentLineId] =
            (quantities[fulfillmentLineId] || 0) + rejection.quantities[fulfillmentLineId];
        });
      });

      for (let fulfillmentLine of fulfillmentData.fulfillment.fulfillment_lines) {
        if (quantities[fulfillmentLine.id] < fulfillmentLine.quantity) {
          areFullyFulfilled[fulfillmentData.fulfillment.id] = false;
        }
      }
    }

    return areFullyFulfilled;
  }, [fulfillmentsData]);

  const isOrderFullyFulfilled = useMemo(() => {
    return !Object.values(areFullyFulfilled).some((value) => !value);
  }, [areFullyFulfilled]);

  const canFulfillAny = useMemo(() => {
    return fulfillmentsData.some((fulfillmentData) => canFulfill(fulfillmentData.fulfillment));
  }, [fulfillmentsData]);

  if (!order) {
    return null;
  }

  function canSubmit() {
    if (!fulfillmentsData || !data || error) {
      return false;
    }

    const hasMultipleFulfillments = fulfillmentsData.length > 1;
    for (const fulfillmentData of fulfillmentsData) {
      const fulfillmentId = fulfillmentData.fulfillment.id;
      const fulfillmentNewData = data[fulfillmentId];
      const quantities = {};

      if (areFullyFulfilled[fulfillmentId]) {
        continue;
      }

      if (!fulfillmentNewData || !fulfillmentNewData.shipments.length) {
        setError(`There are no shipments for Fulfillment #${fulfillmentId}`);
        return false;
      }

      if (fulfillmentNewData.shipments) {
        for (const shipment of fulfillmentNewData.shipments) {
          let totalShipmentQuantities = 0;
          for (const lineId in shipment.quantities) {
            totalShipmentQuantities += shipment.quantities[lineId];
          }

          if (totalShipmentQuantities === 0) {
            continue;
          }

          if (!shipment.shippingMethod) {
            setError(
              hasMultipleFulfillments
                ? `Please select Shipping method in Fulfillment #${fulfillmentId}`
                : 'Please select Shipping method',
            );
            return false;
          }

          if (!shipment.trackingNumber) {
            setError(
              hasMultipleFulfillments
                ? `Please add Tracking Number in Fulfillment #${fulfillmentId}`
                : 'Please add Tracking Number',
            );
            return false;
          }

          for (const lineId in shipment.quantities) {
            quantities[lineId] = (quantities[lineId] || 0) + shipment.quantities[lineId];
          }
        }
      }

      if (fulfillmentNewData.rejection) {
        for (const lineId in fulfillmentNewData.rejection.quantities) {
          quantities[lineId] = (quantities[lineId] || 0) + fulfillmentNewData.rejection.quantities[lineId];
        }
      }

      if (fulfillmentData.shipments) {
        for (const shipment of fulfillmentData.shipments) {
          for (const lineId in shipment.quantities) {
            quantities[lineId] = (quantities[lineId] || 0) + shipment.quantities[lineId];
          }
        }
      }

      if (fulfillmentData.rejections) {
        for (const rejection of fulfillmentData.rejections) {
          for (const lineId in rejection.quantities) {
            quantities[lineId] = (quantities[lineId] || 0) + rejection.quantities[lineId];
          }
        }
      }

      for (const fulfillmentLine of fulfillmentData.fulfillment.fulfillment_lines) {
        if (quantities[fulfillmentLine.id] > (fulfillmentLine.original_quantity || fulfillmentLine.quantity)) {
          setError(`You have specified a total of ${quantities[fulfillmentLine.id]}, but can only fulfill ${
            fulfillmentLine.quantity
          } \
                        for SKU: ${fulfillmentLine.orderLine.variant.sku}`);
          return false;
        }
      }
    }

    return true;
  }

  function submit() {
    props.submit(data, getFulfillmentLinesIdentifiers(fulfillmentsData));
    onClose();
  }

  return (
    <>
      <ModalWindow open={opened} onClose={onClose}>
        <h2 className={styles.title}>
          Order Fulfill <span className={styles.retailerOrderId}>(PO#: {retailer_order_id})</span>
        </h2>

        <hr />

        <div className={styles.main}>
          {fulfillmentsData.map((fulfillmentData) => (
            <FulfillmentFulfillForm
              key={fulfillmentData.fulfillment.id}
              fulfillment={fulfillmentData.fulfillment}
              previousShipments={fulfillmentData.shipments}
              previousRejections={fulfillmentData.rejections}
              onUpdate={onUpdate}
              isFullyFulfilled={areFullyFulfilled[fulfillmentData.fulfillment.id]}
              singleFulfillment={fulfillmentsData.length === 1}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <div className={styles.errorMessage}>{error}</div>

          <div className={styles.actions}>
            <Button text="Close" onClick={onClose} color="red" />

            {!isOrderFullyFulfilled && canFulfillAny && (
              <Button text="Submit" onClick={submit} disabled={!canSubmit()} />
            )}
          </div>
        </div>
      </ModalWindow>
    </>
  );
}

export default SupplierOrderFulfillModal;

SupplierOrderFulfillModal.defaultProps = {
  opened: false,
};

SupplierOrderFulfillModal.propTypes = {
  opened: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.any,
  supplierId: PropTypes.number.isRequired,
};
